import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";

import { apiErrorDetail } from "../../api/client";
import { ToastContext, type ToastApi, type ToastOptions, type ToastTone } from "./context";

interface Toast {
  id: string;
  /** Changes on every show, so replacing a toast by id restarts its timer. */
  seq: number;
  message: string;
  tone: ToastTone;
  durationMs: number;
  action?: ToastOptions["action"];
}

const MAX_VISIBLE = 3;
const DEFAULT_MS = 4000;
const LONG_MS = 8000;

let counter = 0;

// Literal class names — Tailwind keeps a component class only if its exact
// string appears in source.
const TONE_CLASS: Record<ToastTone, string> = {
  info: "toast--info",
  ok: "toast--ok",
  error: "toast--error",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((message: string, options: ToastOptions = {}) => {
    const tone = options.tone ?? "info";
    const id = options.id ?? `toast-${++counter}`;
    const durationMs =
      options.durationMs ?? (tone === "error" || options.action ? LONG_MS : DEFAULT_MS);
    setToasts((list) => {
      const next = list.filter((t) => t.id !== id);
      next.push({ id, seq: ++counter, message, tone, durationMs, action: options.action });
      return next.slice(-MAX_VISIBLE);
    });
    return id;
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      ok: (message, options) => show(message, { ...options, tone: "ok" }),
      error: (error, options) =>
        show(typeof error === "string" ? error : apiErrorDetail(error), { ...options, tone: "error" }),
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-region" aria-live="polite">
          {toasts.map((t) => (
            <ToastItem key={t.seq} toast={t} dismiss={dismiss} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, dismiss }: { toast: Toast; dismiss: (id: string) => void }) {
  const { id } = toast;
  const onDismiss = useCallback(() => dismiss(id), [dismiss, id]);
  const [paused, setPaused] = useState(false);
  const remaining = useRef(toast.durationMs);
  const startedAt = useRef(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (paused) return;
    startedAt.current = performance.now();
    const timer = window.setTimeout(onDismiss, remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current -= performance.now() - startedAt.current;
    };
  }, [paused, onDismiss]);

  return (
    <div
      className={clsx("toast", TONE_CLASS[toast.tone])}
      role={toast.tone === "error" ? "alert" : "status"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="toast-message">{toast.message}</span>
      {toast.action ? (
        <button
          type="button"
          className="toast-action"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await toast.action?.onClick();
            } finally {
              onDismiss();
            }
          }}
        >
          {toast.action.label}
        </button>
      ) : null}
      <button type="button" className="toast-close" aria-label="Dismiss" onClick={onDismiss}>
        ×
      </button>
    </div>
  );
}
